(() => {
  "use strict";

  const supportsDialog =
    typeof HTMLDialogElement === "function" &&
    "showModal" in HTMLDialogElement.prototype;

  const CLOSING_TIMER = Symbol("closingTimer");

  // --- NEW: global click-suppression to prevent click-throughs ---
  const BACKDROP_SUPPRESS_MS = 180; // small safety window
  let suppressClicksUntil = 0;
  function suppressClickCapture(e) {
    if (performance.now() < suppressClicksUntil) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }
  // Capture-phase listener runs before anything else on the page
  document.addEventListener("click", suppressClickCapture, true);

  function getPanel(dlg) {
    return dlg.querySelector("[data-panel]") || dlg.querySelector(".tw\\:transform");
  }
  function forceReflow(el) { void el.offsetHeight; }

  function actuallyOpen(dlg) {
    if (supportsDialog) dlg.showModal();
    else { dlg.setAttribute("open", ""); dlg.hidden = false; }
  }
  function clearClosingTimer(dlg) {
    if (dlg[CLOSING_TIMER]) { clearTimeout(dlg[CLOSING_TIMER]); dlg[CLOSING_TIMER] = null; }
  }
  function actuallyClose(dlg) {
    if (supportsDialog) dlg.close();
    else { dlg.removeAttribute("open"); dlg.hidden = true; }
    clearClosingTimer(dlg);
  }

  function clearInputs(dlg) {
    const focusTarget = dlg.querySelector("[data-auto-focus]") || 
                  dlg.querySelector("input, textarea, select, [tabindex]:not([tabindex='-1'])");
                  console.log(focusTarget);
    if (focusTarget) {
        focusTarget.value = "";
      }
  }
  function openDialog(dlg) {
    const panel = getPanel(dlg);
    if (panel) { panel.setAttribute("data-closed", ""); forceReflow(panel); }
    actuallyOpen(dlg);
    requestAnimationFrame(() => { 
      if (panel) panel.removeAttribute("data-closed"); 
      
      const isMobile = navigator.userAgentData.mobile; 
      const focusTarget = dlg.querySelector("[data-auto-focus]") || 
                    dlg.querySelector("input, textarea, select, [tabindex]:not([tabindex='-1'])");
      if (focusTarget && !isMobile) {
        focusTarget.focus({ preventScroll: true });
      }

    });
  }

  function closeDialog(dlg) {
    const panel = getPanel(dlg);
    if (!panel) { actuallyClose(dlg); return; }
    panel.setAttribute("data-closed", "");
    const onEnd = (e) => {
      if (e.target !== panel) return;
      if (e.propertyName && e.propertyName !== "transform") return;
      panel.removeEventListener("transitionend", onEnd);
      actuallyClose(dlg);
      clearInputs(dlg); 
    };
    panel.addEventListener("transitionend", onEnd, { once: true });
    dlg[CLOSING_TIMER] = setTimeout(() => {
      panel.removeEventListener("transitionend", onEnd);
      actuallyClose(dlg);
      clearInputs(dlg);
    }, 50);
  }

  // Delegated open/close (supports your legacy [command] attrs)
  function onDocumentClick(e) {
    const openBtn = e.target.closest("[data-dialog-open],[command='show-modal']");
    if (openBtn) {
      const id = openBtn.getAttribute("data-dialog-open") || openBtn.getAttribute("commandfor");
      const dlg = id && document.getElementById(id);
      if (dlg) openDialog(dlg);
      return;
    }
    const closeBtn = e.target.closest("[data-dialog-close],[command='close']");
    if (closeBtn) {
      const id = closeBtn.getAttribute("data-dialog-close") || closeBtn.getAttribute("commandfor");
      const dlg = (id && document.getElementById(id)) || closeBtn.closest("dialog");
      if (dlg) closeDialog(dlg);
      return;
    }
  }

  // --- UPDATED: robust backdrop handling to avoid click-throughs ---
  function attachBackdropGuards(dlg) {
    // 1) On pointerdown outside the panel, consume the event & arm suppression
    dlg.addEventListener("pointerdown", (e) => {
      const panel = getPanel(dlg); if (!panel) return;
      const r = panel.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right &&
                     e.clientY >= r.top  && e.clientY <= r.bottom;
      if (!inside) {
        suppressClicksUntil = performance.now() + BACKDROP_SUPPRESS_MS;
        e.preventDefault();
        e.stopPropagation();
        setTimeout(() => closeDialog(dlg), 0);
      }
    });

    // 2) On click outside the panel, econsume & close AFTER the currnt turn
    dlg.addEventListener("click", (e) => {
      const panel = getPanel(dlg); if (!panel) return;
      const r = panel.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right &&
                     e.clientY >= r.top  && e.clientY <= r.bottom;
      if (!inside) {
        suppressClicksUntil = performance.now() + BACKDROP_SUPPRESS_MS;
        e.preventDefault();
        e.stopPropagation();
        // Delay close so the overlay remains until the click is fully handled
        setTimeout(() => closeDialog(dlg), 0);
      }
    });
  }

  function onCancel(e) {
    const dlg = e.target;
    const panel = getPanel(dlg);
    if (panel) panel.setAttribute("data-closed", "");
    // Also suppress the ESC-triggered "click" sequences on some UAs
    suppressClicksUntil = performance.now() + BACKDROP_SUPPRESS_MS;
  }

  function onClose(e) {
    const dlg = e.target;
    clearClosingTimer(dlg);
    const panel = getPanel(dlg);
    if (panel) panel.setAttribute("data-closed", "");
  }

  function init() {
    document.addEventListener("click", onDocumentClick);

    document.querySelectorAll("dialog").forEach((dlg) => {
      attachBackdropGuards(dlg);
      dlg.addEventListener("cancel", onCancel);
      dlg.addEventListener("close", onClose);

      const panel = getPanel(dlg);
      if (panel) panel.setAttribute("data-closed", "");
    });

    // ESC for non-modal fallback
    if (!supportsDialog) {
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          document.querySelectorAll("dialog[open]").forEach((dlg) => closeDialog(dlg));
          suppressClicksUntil = performance.now() + BACKDROP_SUPPRESS_MS;
        }
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  // Optional API
  window.DHDrawer = {
    open: (idOrEl) => openDialog(typeof idOrEl === "string" ? document.getElementById(idOrEl) : idOrEl),
    close: (idOrEl) => closeDialog(typeof idOrEl === "string" ? document.getElementById(idOrEl) : idOrEl),
  };
})();

(function preventOpenOnFilterRemove() {
  const colorContainer = document.getElementById("color-container");
  if (colorContainer) {
    colorContainer.addEventListener("click", (e) => {
    if (e.target.closest("#color-remove")) {
      e.stopPropagation();
    }
  });
  }
})();

(function preventOpenOnFilterRemove2() {
  const aromaContainer = document.getElementById("aroma-container");
  if (aromaContainer) {
    aromaContainer.addEventListener("click", (e) => {
      if (e.target.closest('#aroma-remove')) {
        e.stopPropagation();
      }
    });
  }
  
})();

(function preventOpenOnFilterRemove3() {
  const nicotinContainer = document.getElementById("nicotin-container");
  if(nicotinContainer) {
    nicotinContainer.addEventListener("click", (e) => {
      if (e.target.closest("#nicotin-remove")) {
        e.stopPropagation();
      }
    });
  }
  
})();